import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, MailCheck, Search, FileSpreadsheet, ArrowUpDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";

const PAGE_SIZE = 50;
const DAY = 86400000;

const SOURCE_LABEL = { signup: "Signup", account_settings: "Account settings" };
const SYNC_LABEL = {
  ok: "Synced",
  error: "Sync error",
  disabled: "Not synced (off)",
  resubscribe_required: "Resubscribe needed",
};
const SYNC_CLS = {
  ok: "bg-teal-50 text-teal-700",
  error: "bg-red-50 text-red-700",
  disabled: "bg-secondary text-muted-foreground",
  resubscribe_required: "bg-amber-50 text-amber-700",
};

const stamp = (iso) => {
  if (!iso) return "—";
  try { return format(parseISO(iso), "d MMM yyyy, HH:mm"); } catch { return iso; }
};

const Stat = ({ label, value, testId, accent }) => (
  <Card className="p-3.5" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`mt-0.5 text-2xl font-[680] tracking-[-0.01em] ${accent || "text-foreground"}`}>{value}</div>
  </Card>
);

export default function AdminConsentLog() {
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState("all");
  const [source, setSource] = useState("all");
  const [syncResult, setSyncResult] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const filterParams = useCallback(() => {
    const p = {};
    const now = Date.now();
    if (datePreset === "24h") p.start = new Date(now - DAY).toISOString();
    else if (datePreset === "7d") p.start = new Date(now - 7 * DAY).toISOString();
    else if (datePreset === "30d") p.start = new Date(now - 30 * DAY).toISOString();
    else if (datePreset === "custom") {
      if (customStart) p.start = customStart;
      if (customEnd) p.end = customEnd;
    }
    if (eventType !== "all") p.event_type = eventType;
    if (source !== "all") p.source = source;
    if (syncResult !== "all") p.sync_result = syncResult;
    if (sort !== "newest") p.sort = sort;
    if (q) p.q = q;
    return p;
  }, [datePreset, customStart, customEnd, eventType, source, syncResult, sort, q]);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/admin/marketing/consent-events", {
        params: { page, page_size: PAGE_SIZE, ...filterParams() },
      });
      setData(data);
    } catch {
      setData({ events: [], total: 0, summary: {} });
    }
  }, [page, filterParams]);
  useEffect(() => { load(); }, [load]);

  const reset = () => setPage(1);
  const applySearch = () => { reset(); setQ(qInput.trim()); };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/marketing/consent-events/export", {
        params: filterParams(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "marketing-consent-log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Consent log exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <Loading />;
  const s = data.summary || {};
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div data-testid="admin-consent-log">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
            <MailCheck className="h-5 w-5 text-primary" /> Consent log
          </h2>
          <p className="text-xs text-muted-foreground">
            Append-only record of every marketing email opt-in and opt-out. Read-only — entries are never edited or deleted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={exporting || (data.total || 0) === 0}
            onClick={doExport} data-testid="consent-export-csv">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />{exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <AdminRefreshButton testId="admin-refresh-consent" onRefresh={load} />
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Opt-ins (filtered)" value={s.opt_in ?? 0} accent="text-teal-700" testId="consent-summary-optins" />
        <Stat label="Opt-outs (filtered)" value={s.opt_out ?? 0} testId="consent-summary-optouts" />
        <Stat label="Consent entries" value={s.total ?? 0} testId="consent-summary-total" />
        <Stat label="Subscribed right now" value={s.subscribers_now ?? 0} accent="text-teal-700" testId="consent-summary-subscribers" />
      </div>

      {data.mailerlite_enabled === false && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-border/70 bg-secondary/50 p-3 text-xs text-muted-foreground" data-testid="consent-mailerlite-off">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>MailerLite sync is switched off in this environment, so consent is recorded locally only. The trail below is still complete and authoritative.</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select value={eventType} onValueChange={(v) => { reset(); setEventType(v); }}>
          <SelectTrigger className="w-36" data-testid="consent-filter-type"><SelectValue placeholder="Consent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All consent</SelectItem>
            <SelectItem value="opt_in">Opt-in</SelectItem>
            <SelectItem value="opt_out">Opt-out</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(v) => { reset(); setSource(v); }}>
          <SelectTrigger className="w-40" data-testid="consent-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {(data.sources || []).map((x) => <SelectItem key={x} value={x}>{SOURCE_LABEL[x] || x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={syncResult} onValueChange={(v) => { reset(); setSyncResult(v); }}>
          <SelectTrigger className="w-44" data-testid="consent-filter-sync"><SelectValue placeholder="Sync" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sync results</SelectItem>
            {(data.sync_results || []).map((x) => <SelectItem key={x} value={x}>{SYNC_LABEL[x] || x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={(v) => { reset(); setDatePreset(v); }}>
          <SelectTrigger className="w-36" data-testid="consent-filter-date"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="24h">Last 24 hours</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => { reset(); setSort(v); }}>
          <SelectTrigger className="w-32" data-testid="consent-filter-sort">
            <ArrowUpDown className="mr-1 h-3.5 w-3.5 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder="Search email or name…"
            className="w-52"
            data-testid="consent-search-input"
          />
          <Button size="sm" variant="secondary" onClick={applySearch} data-testid="consent-search-button"><Search className="h-4 w-4" /></Button>
        </div>
      </div>

      {datePreset === "custom" && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border/60 bg-secondary/40 px-3 py-2" data-testid="consent-custom-range">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={customStart} onChange={(e) => { reset(); setCustomStart(e.target.value); }} className="w-40" data-testid="consent-date-start" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={customEnd} onChange={(e) => { reset(); setCustomEnd(e.target.value); }} className="w-40" data-testid="consent-date-end" />
          </div>
        </div>
      )}

      {(data.total || 0) === 0 ? (
        <EmptyState icon={MailCheck} title="No consent entries" description="Nothing matches the current filters yet." testId="admin-consent-empty" />
      ) : (
        <>
          <Card className="border-0 p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Consent</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>MailerLite</TableHead>
                    <TableHead>Status now</TableHead>
                    <TableHead>Consent text</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.events.map((e) => (
                    <TableRow key={e.id} data-testid={`consent-row-${e.id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground" title={e.consent_timestamp || e.created_at}>
                        {stamp(e.consent_timestamp || e.created_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="text-foreground">{e.email_address}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {e.user_name || (e.user_exists ? "—" : "account removed")}
                          {e.user_id && e.user_exists && (
                            <>
                              {" · "}
                              <Link to={`/dashboard/admin/users/${e.user_id}`} className="font-[560] text-primary hover:underline" data-testid={`consent-user-link-${e.id}`}>
                                {e.user_id}
                              </Link>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={e.consent_granted ? "bg-teal-50 text-teal-700" : "bg-secondary text-muted-foreground"} data-testid={`consent-type-${e.id}`}>
                          {e.consent_granted ? "Opt-in" : "Opt-out"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{SOURCE_LABEL[e.consent_source] || e.consent_source}</TableCell>
                      <TableCell className="text-sm">
                        <Badge className={SYNC_CLS[e.mailerlite_sync_result] || "bg-secondary text-muted-foreground"}>
                          {SYNC_LABEL[e.mailerlite_sync_result] || e.mailerlite_sync_result || "—"}
                        </Badge>
                        {e.sanitized_error_code && <div className="mt-0.5 font-mono text-[11px] text-red-600">{e.sanitized_error_code}</div>}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`consent-status-now-${e.id}`}>
                        {e.currently_subscribed === null || e.currently_subscribed === undefined ? (
                          <span className="text-muted-foreground">—</span>
                        ) : e.currently_subscribed ? (
                          <span className="font-[560] text-teal-700">Subscribed</span>
                        ) : (
                          <span className="text-muted-foreground">Not subscribed</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">
                        {e.consent_text_version || "—"}
                        <div>privacy {e.privacy_notice_version || "—"}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{data.total} entries</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="consent-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
              <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="consent-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
